// The Newsstand — pure core (design_documents/news.md). The worker's /news wire
// becomes a daily "issue": stories matched against what the user tracks
// (library titles, followed people), a lead story and cover lines, previews
// from their own release schedule, and a Readers' Chart from their History.

export const NEWS_CATEGORIES = [
	{ value: "games", label: "Games", color: "#8b3dff", text: "#fff" },
	{ value: "film_tv", label: "Film & TV", color: "#e5383b", text: "#fff" },
	{ value: "anime", label: "Anime", color: "#ff3d8b", text: "#fff" },
	{ value: "comics", label: "Comics", color: "#1fbf5f", text: "#0b0b0f" },
	{ value: "books", label: "Books", color: "#ff7a1a", text: "#0b0b0f" },
];

export function categoryOf(value) {
	return NEWS_CATEGORIES.find((c) => c.value === value) ?? { value, label: value, color: "#9a9aa2", text: "#0b0b0f" };
}

// "Issue Nº 287" — one issue a day, counted from 1 January 2026.
const EPOCH = Date.UTC(2026, 0, 1);
export function issueNumber(today) {
	return Math.floor((Date.parse(`${today}T00:00:00Z`) - EPOCH) / 86400000) + 1;
}

export function issueDateLabel(today) {
	return new Date(`${today}T12:00:00Z`)
		.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", year: "numeric", timeZone: "UTC" })
		.replace(",", "")
		.toUpperCase();
}

// ---------- matching stories to the library ----------

const norm = (s) =>
	String(s ?? "")
		.normalize("NFKD")
		.replace(/[̀-ͯ]/g, "")
		.toLowerCase()
		.replace(/[’'`]/g, "")
		.replace(/[^a-z0-9]+/g, " ")
		.trim();

// Words too common to stand alone as a title match ("Control", "Inside"…).
const COMMON = new Set([
	"control", "inside", "journey", "alien", "aliens", "dune", "it", "us", "her", "him", "them", "up", "home", "heat",
	"cars", "hades", "prey", "brave", "frozen", "soul", "coco", "rush", "drive", "split", "glass", "unity", "origins",
	"the", "you", "lost", "house", "friends", "dark", "gone", "trust", "sleep", "hunt", "fire", "blood", "season",
	"review", "reviews", "trailer", "news", "update", "book", "books", "game", "games", "movie", "film", "show", "series",
]);

// The phrase a title is matched by: subtitles after ":" are kept (they make it
// more specific); a leading "The " is optional.
function titlePhrase(title) {
	return norm(title).replace(/^the /, "");
}

// Can this phrase be matched safely? Multi-word phrases of ≥ 6 characters, or a
// single uncommon word of ≥ 6 characters (matched in the HEADLINE only).
function matchable(phrase) {
	if (!phrase || phrase.length < 4) return null;
	const words = phrase.split(" ");
	if (words.length >= 2 && phrase.length >= 6) return { headlineOnly: false };
	if (words.length === 1 && phrase.length >= 6 && !COMMON.has(phrase)) return { headlineOnly: true };
	return null;
}

// Build the matchers once per issue.
//   library — normalized items (db/media.js: title, route, media_type, mediaLabel)
//   people  — followed people ({ name, route })
export function buildMatchers({ library = [], people = [] } = {}) {
	const out = [];
	for (const item of library) {
		const phrase = titlePhrase(item.title);
		const rule = matchable(phrase);
		if (!rule) continue;
		out.push({ kind: "library", phrase, headlineOnly: rule.headlineOnly, label: item.title, route: item.route ?? null, media_type: item.media_type ?? null });
	}
	for (const p of people) {
		const phrase = norm(p.name);
		if (phrase.split(" ").length < 2 || phrase.length < 6) continue; // full names only
		out.push({ kind: "follow", phrase, headlineOnly: false, label: p.name, route: p.route ?? null });
	}
	// Longer phrases first: "Hollow Knight: Silksong" beats "Hollow Knight".
	return out.sort((a, b) => b.phrase.length - a.phrase.length);
}

// The first matcher that a story mentions, library before follows; null if none.
export function matchStory(story, matchers) {
	const headline = ` ${norm(story.title)} `;
	const body = ` ${norm(story.summary)} `;
	let follow = null;
	for (const m of matchers) {
		const needle = ` ${m.phrase} `;
		const hit = headline.includes(needle) || (!m.headlineOnly && body.includes(needle));
		if (!hit) continue;
		if (m.kind === "library") return m;
		follow ??= m;
	}
	return follow;
}

// ---------- the issue ----------

// The cover story: a fresh review with an image, else the freshest For You
// match with an image, else the newest story with an image.
export function pickLead(stories, matches = new Map()) {
	const withImage = stories.filter((s) => s.image);
	return (
		withImage.find((s) => s.review) ??
		withImage.find((s) => matches.has(s.id)) ??
		withImage[0] ??
		stories[0] ??
		null
	);
}

// Releases from the user's own schedule within `days` (normalized schedule rows
// with release_date), soonest first.
export function previews(scheduleItems, today, days = 14) {
	const end = new Date(Date.parse(`${today}T00:00:00Z`) + days * 86400000).toISOString().slice(0, 10);
	return (scheduleItems ?? [])
		.filter((x) => x.release_date && x.release_date >= today && x.release_date <= end)
		.sort((a, b) => a.release_date.localeCompare(b.release_date) || (a.title ?? "").localeCompare(b.title ?? ""));
}

// Your top 5 of the last 30 days from History: items you finished or rated in
// the window, ranked by their current rating (then most recent), with movement
// against the 30 days before ("NEW", "▲2", "▼1", "—").
export function readersChart(events, mediaById, today, size = 5) {
	const now = Date.parse(`${today}T23:59:59Z`);
	const day = 86400000;
	const windowItems = (from, to) => {
		const latest = new Map();
		for (const e of events ?? []) {
			if (e.deleted) continue;
			const finished = e.kind === "engagement" && e.to_value === "done";
			if (!finished && e.kind !== "rating") continue;
			const t = Date.parse(e.at);
			if (!(t > from && t <= to)) continue;
			if (!mediaById.has(e.item_id)) continue;
			latest.set(e.item_id, Math.max(latest.get(e.item_id) ?? 0, t));
		}
		return [...latest.entries()]
			.map(([id, t]) => ({ item: mediaById.get(id), t }))
			.filter((x) => (x.item.rating ?? 0) > 0)
			.sort((a, b) => (b.item.rating ?? 0) - (a.item.rating ?? 0) || b.t - a.t)
			.slice(0, size)
			.map((x) => x.item);
	};
	const current = windowItems(now - 30 * day, now);
	const previous = windowItems(now - 60 * day, now - 30 * day);
	return current.map((item, i) => {
		const before = previous.findIndex((p) => p.id === item.id);
		const move = before < 0 ? "NEW" : before === i ? "—" : before > i ? `▲${before - i}` : `▼${i - before}`;
		return { rank: i + 1, item, move };
	});
}

// Short cover lines under the cover story.
export function coverLines({ subscriptionsToday = [], nextPreview = null, forYouCount = 0, rumour = null } = {}) {
	const lines = [];
	if (subscriptionsToday[0]) lines.push({ kicker: "PLUS!", text: `${subscriptionsToday[0].label} hits shelves today`, color: "#22d3ee" });
	else if (nextPreview) lines.push({ kicker: "PLUS!", text: `${nextPreview.title} is coming soon`, color: "#22d3ee" });
	if (forYouCount > 0) lines.push({ kicker: "INSIDE:", text: `${forYouCount} thing${forYouCount === 1 ? "" : "s"} you track made the news`, color: "#b6f23d" });
	if (rumour) lines.push({ kicker: "RUMOUR MILL:", text: shorten(rumour.title, 52), color: "#ffe600" });
	return lines;
}

// Cut at a word boundary with an ellipsis.
export function shorten(text, max) {
	const s = String(text ?? "");
	if (s.length <= max) return s;
	const cut = s.slice(0, max - 1);
	const space = cut.lastIndexOf(" ");
	return `${(space > max / 2 ? cut.slice(0, space) : cut).replace(/[s,;:–—-]+$/, "")}…`;
}

export function timeAgo(iso, now = Date.now()) {
	const t = Date.parse(iso ?? "");
	if (!Number.isFinite(t)) return "";
	const mins = Math.max(0, Math.round((now - t) / 60000));
	if (mins < 60) return `${mins}m ago`;
	const hours = Math.round(mins / 60);
	if (hours < 24) return `${hours}h ago`;
	return `${Math.round(hours / 24)}d ago`;
}
